import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Menu, MenuTrigger, MenuPopover, MenuList } from '@fluentui/react-components';
import { PatchTask } from '../../redux/taskSlice';
import styles from './css/index.module.css';

/**
 * TimelineRangePickerMenu
 * Edits Task.start_date and Task.end_date (both optional).
 */
const TimelineRangePickerMenu = ({ task, onTimelineSelect, children }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [startDate, setStartDate] = useState(task?.start_date || '');
    const [endDate, setEndDate] = useState(task?.end_date || '');

    useEffect(() => {
        if (task?.start_date) {
            setStartDate(task.start_date);
        } else {
            // Pre-set start date to today's date when not already set
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            setStartDate(`${yyyy}-${mm}-${dd}`);
        }
        setEndDate(task?.end_date || '');
    }, [task?.id, task?.start_date, task?.end_date]);

    const isInvalidRange = Boolean(startDate && endDate && startDate > endDate);

    const handleSaveTimeline = async () => {
        if (!task?.id) return;
        if (isInvalidRange) return;

        try {
            await dispatch(
                PatchTask({
                    body: {
                        start_date: startDate || null,
                        end_date: endDate || null,
                    },
                    id: task.id,
                    suppressToast: true,
                }),
            );
            onTimelineSelect && onTimelineSelect({ start_date: startDate || null, end_date: endDate || null });
            setOpen(false);
        } catch (error) {
            console.error('Error updating task timeline:', error);
        }
    };

    const handleClearTimeline = async () => {
        if (!task?.id) return;
        try {
            await dispatch(
                PatchTask({
                    body: {
                        start_date: null,
                        end_date: null,
                    },
                    id: task.id,
                    suppressToast: true,
                }),
            );
            setStartDate('');
            setEndDate('');
            onTimelineSelect && onTimelineSelect({ start_date: null, end_date: null });
            setOpen(false);
        } catch (error) {
            console.error('Error clearing task timeline:', error);
        }
    };

    return (
        <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
            <MenuTrigger>{children}</MenuTrigger>
            <MenuPopover style={{ width: '320px', minWidth: '320px' }}>
                <MenuList>
                    <div className="p-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <span className={styles.tableFilterHeader}>Set Timeline</span>
                        </div>

                        <div className="mb-3">
                            <label className="form-label small mb-0" style={{ fontSize: '10px' }}>
                                Start date
                            </label>
                            <input
                                type="date"
                                className={'form-control form-control-sm ' + styles.filterSelect}
                                value={startDate || ''}
                                onChange={e => setStartDate(e.target.value)}
                            />
                        </div>

                        <div className="mb-2">
                            <label className="form-label small mb-0" style={{ fontSize: '10px' }}>
                                End date
                            </label>
                            <input
                                type="date"
                                className={'form-control form-control-sm ' + styles.filterSelect}
                                value={endDate || ''}
                                onChange={e => setEndDate(e.target.value)}
                            />
                        </div>

                        {isInvalidRange && (
                            <div style={{ fontSize: '11px', color: '#c50f1f', marginBottom: 8 }}>
                                Start date must be on/before end date.
                            </div>
                        )}

                        <div className="d-flex gap-2 justify-content-between">
                            <span
                                className={styles.secondaryBtnText + ' ms-1'}
                                onClick={handleSaveTimeline}
                                style={{
                                    opacity: isInvalidRange ? 0.5 : 1,
                                    cursor: isInvalidRange ? 'not-allowed' : 'pointer',
                                }}>
                                Save timeline
                            </span>
                            <span className={styles.secondaryBtnText + ' ms-1'} onClick={handleClearTimeline}>
                                Clear
                            </span>
                        </div>
                    </div>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default TimelineRangePickerMenu;

